<template>
  <transition name="fade">
    <div v-if="isShowing">
      <div
        :id="modalType"
        class="modal fade show clear-margin"
        :class="animation ? `animation-${animation}` : ''"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="modal-dialog modal-dialog-centered"
          :class="[modalSize, { 'modal-dialog-scrollable': isScrollable === true }]"
        >
          <div class="modal-content position-relative" :class="backgroundColor">
            <div v-if="isHeaderOn" class="modal-header" :class="[headerColor, headerClass]">
              <slot name="header" />
              <h5 v-if="title" class="modal-title font-normal" id="primary-header-modalLabel" :class="titleClass">
                {{ title }}
              </h5>
              <button
                v-if="isClosable"
                type="button"
                class="border-0 bg-transparent"
                :class="closeButtonClass"
                @click="$emit('close')"
              >
                <span class="d-flex justify-content-center align-items-center rounded-circle icon-close">
                  <close-icon />
                </span>
              </button>
            </div>
            <div class="modal-body" :class="modalBodyClass">
              <slot name="body" />
            </div>
            <div class="modal-footer" :class="footerClass" v-if="$slots['footer']">
              <slot name="footer" />
            </div>
          </div>
          <!-- /.modal-content -->
        </div>
        <!-- /.modal-dialog -->
      </div>
      <!-- /.modal -->
      <div class="modal-backdrop fade show" />
    </div>
  </transition>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue'

// #region - importing components
import CloseIcon from '@/components/icons/CloseIcon.vue'
// endregion

const props = defineProps({
  type: {
    type: String,
    required: false,
    default: 'primary'
  },
  title: {
    type: String
  },
  isShowing: {
    type: Boolean,
    required: false,
    default: false
  },
  coloredHeader: {
    type: Boolean,
    required: false,
    default: false
  },
  headerClass: {
    type: String,
    required: false,
    default: ''
  },
  modalSize: {
    type: String,
    required: false,
    default: ''
  },
  modalBodyClass: {
    type: String,
    required: false,
    default: ''
  },
  isHeaderOn: {
    type: Boolean,
    required: false,
    default: true
  },
  isClosable: {
    type: Boolean,
    required: false,
    default: true
  },
  footerClass: {
    type: [String, Array, Object],
    required: false,
    default: ''
  },
  textColor: {
    type: String,
    default: 'text-muted',
    requred: false
  },
  closeButtonClass: {
    type: String,
    default: '',
    required: false
  },
  titleClass: {
    type: String,
    default: '',
    required: false
  },
  isScrollable: {
    type: Boolean,
    default: false,
    required: false
  },
  animation: {
    type: String,
    required: false,
    default: 'fade-scale',
    validator: value => ['', 'fade-scale'].includes(value)
  }
})

const modalType = computed(() => `${props.type}-header-modal`)

const backgroundColor = computed(() => {
  if (props.coloredHeader) {
    return ''
  }
  if (props.type === 'success' || props.type === 'danger') {
    return `bg-${props.type} modal-filled`
  } else {
    return ``
  }
})

const headerColor = computed(() => {
  if (!props.coloredHeader) {
    return ''
  }

  if (props.type === 'success' || props.type === 'danger' || props.type === 'indigo' || props.type === 'info') {
    return `bg-${props.type} modal-colored-header`
  } else {
    return ''
  }
})

const toggleBodyClass = (method, bodyClass) => {
  const el = document.body

  if (method === 'add') {
    el.classList.add(bodyClass)
  } else {
    el.classList.remove(bodyClass)
  }
}

// #region - scroll lock helpers
const lockModalBodyScroll = () => {
  document.body.style.overflow = 'hidden'
  document.body.style.touchAction = 'none'
}

const unlockModalBodyScroll = () => {
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
}
// endregion

watch(
  () => props.isShowing,
  newValue => {
    if (newValue === true) {
      toggleBodyClass('add', 'modal-open')
      lockModalBodyScroll()
    } else {
      toggleBodyClass('remove', 'modal-open')
      unlockModalBodyScroll()
    }
  }
)

// #region - cleanup on unmount
onBeforeUnmount(() => {
  unlockModalBodyScroll()
})
// endregion
</script>

<style scoped>
.modal {
  &.show {
    display: block;
    overscroll-behavior: contain;
  }

  .modal-content {
    overscroll-behavior: contain;
  }

  .close {
    position: absolute;
    top: 10px;
    right: 11px;

    & i {
      font-size: 21.336px;
    }
  }

  .modal-header {
    justify-content: space-between;
  }

  .modal-footer {
    margin-bottom: 20px !important;
  }
}

.slimScrollDiv {
  height: auto;
  max-height: 750px;
  overflow-x: hidden;
}

.text-indigo {
  color: var(--text-bg-info-light);
}

.icon-close {
    background-color: var(--primary-disabled);
    display: block;
    width: 20px;
    height: 20px;
}
</style>
